import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

interface HotelInfo {
  id: string;
  name: string;
  description: string;
  address: string;
  phone: string;
  email: string;
  check_in_time: string;
  check_out_time: string;
  policies: {
    cancellation_policy: string;
    check_in_policy: string;
    pets_policy: string;
    smoking_policy: string;
  };
  amenities: string[];
}

interface UseRealtimeHotelInfoReturn {
  hotelInfo: HotelInfo | null;
  isLoading: boolean;
  error: string | null;
}

export const useRealtimeHotelInfo = (hotelSlug: string): UseRealtimeHotelInfoReturn => {
  const [hotelInfo, setHotelInfo] = useState<HotelInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHotelInfo = useCallback(async () => {
    try {
      setError(null);
      
      // First try to use hotelSlug as a direct UUID if it matches the pattern
      const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      let query = supabase
        .from('hotels')
        .select(`
          id,
          name,
          description,
          address,
          phone,
          email,
          check_in_time,
          check_out_time,
          policies,
          amenities
        `);
      
      if (uuidPattern.test(hotelSlug)) {
        console.log('🔍 Using provided hotelSlug as direct hotel ID');
        query = query.eq('id', hotelSlug);
      } else {
        console.log('🔍 Looking up hotel by name (case insensitive)');
        query = query.ilike('name', `%${hotelSlug}%`);
      }
      
      // Use maybeSingle() instead of single() to handle 0 or multiple rows gracefully
      const { data, error: fetchError } = await query.maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      if (!data) {
        console.log('⚠️ Hotel not found by ID/name, attempting to get first available hotel');
        // If specific hotel not found, get first hotel
        const { data: anyHotel, error: anyError } = await supabase
          .from('hotels')
          .select(`
            id,
            name,
            description,
            address,
            phone,
            email,
            check_in_time,
            check_out_time,
            policies,
            amenities
          `)
          .limit(1)
          .single();

        if (anyError) throw anyError;
        console.log('🏨 Using fallback hotel:', anyHotel?.name);
        setHotelInfo(anyHotel);
        return;
      }

      console.log('🏨 Fetched hotel info:', data?.name);
      setHotelInfo(data);
    } catch (err) {
      console.error('❌ Error fetching hotel info:', err);
      setError(err instanceof Error ? err.message : 'Failed to load hotel information');
    } finally {
      setIsLoading(false);
    }
  }, [hotelSlug]); // Only depend on hotelSlug

  useEffect(() => {
    if (!hotelSlug) {
      setIsLoading(false);
      return;
    }

    fetchHotelInfo();
  }, [hotelSlug, fetchHotelInfo]);

  useEffect(() => {
    // Set up real-time subscription only after hotelInfo is loaded
    if (!hotelInfo?.id) return;

    // Set up real-time subscription for hotel changes
    console.log('🔗 Setting up real-time subscription for hotel info');
    
    const channel = supabase
      .channel(`hotel-info-changes-${hotelInfo.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'hotels',
          filter: `id=eq.${hotelInfo.id}`
        },
        (payload) => {
          console.log('🔄 Real-time hotel info update:', payload.new);
          setHotelInfo(payload.new as HotelInfo);
        }
      )
      .subscribe((status) => {
        console.log('📡 Hotel info subscription status:', status);
      });

    return () => {
      console.log('🔌 Cleaning up hotel info real-time subscription');
      supabase.removeChannel(channel);
    };
  }, [hotelInfo?.id]); // Only depend on hotel ID

  return { hotelInfo, isLoading, error };
};
